import { useState, useMemo, useCallback } from 'react';
import { Table, TableSortLabel } from '@mui/material';
import { TABLE_TITLES } from '../../../utils/constants/reports/violationReports.constants';

import {
  StyledTableContainer,
  StyledTableHead,
  StyledRowHead,
  StyledTableCell,
  TitleTable,
} from './ViolationsTable.styles';

import {
  getComparator,
  getViolationsWithEmployeeInfo,
  stableSort,
} from '../../../utils/helpers/reports/helperViolationsReports';
import ViolationsTableContent from '../ViolationsTableContent/ViolationsTableContent';
import { useSelector } from 'react-redux';
import { selectAllEmployees } from '../../../redux/slices/employeesSlice';

const ViolationsTable = ({ violations = [], onViolationClick, isLoading }) => {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('timestamp');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const employees = useSelector(selectAllEmployees);

  // Sorting
  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      setPage(0); // Reset page on sort
    },
    [order, orderBy]
  );

  // Pagination
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Sort violations
  const sortedViolations = useMemo(
    () => stableSort(violations, getComparator(order, orderBy)),
    [violations, order, orderBy]
  );

  // Paginate
  const paginatedViolations = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedViolations.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedViolations, page, rowsPerPage]);

  // Add employee info
  const violationsWithEmployeeInfo = useMemo(
    () => getViolationsWithEmployeeInfo(paginatedViolations, employees),
    [paginatedViolations, employees]
  );

  return (
    <StyledTableContainer>
      <Table aria-label="violations table">
        <StyledTableHead>
          <StyledRowHead>
            {TABLE_TITLES.map(({ id, label }) => (
              <StyledTableCell key={id}>
                <TableSortLabel
                  active={orderBy === id}
                  direction={orderBy === id ? order : 'asc'}
                  onClick={() => handleRequestSort(id)}
                >
                  <TitleTable variant="calloutRegular">{label}</TitleTable>
                </TableSortLabel>
              </StyledTableCell>
            ))}
          </StyledRowHead>
        </StyledTableHead>

        <ViolationsTableContent
          isLoading={isLoading}
          sortedViolations={violationsWithEmployeeInfo}
          tableColumnsCount={TABLE_TITLES.length}
          onViolationClick={onViolationClick}
          totalCount={violations.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          isPaginated={violations.length > rowsPerPage}
        />
      </Table>
    </StyledTableContainer>
  );
};

export default ViolationsTable;
